import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocationContext } from '../contexts/LocationContext';

const LocationSwitcher = () => {
  const { locations, currentLocation, switchLocation, addLocation } = useLocationContext();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSwitch = (loc) => {
    switchLocation(loc);
    setOpen(false);
  };

  const handleAddLocation = async () => {
    try {
      await addLocation({ business_name: 'New Location' });
      setOpen(false);
      navigate('/settings');
    } catch {
      // error handled in context
    }
  };

  if (!currentLocation) return null;

  const displayName = currentLocation.city
    ? `${currentLocation.business_name} - ${currentLocation.city}`
    : currentLocation.business_name;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none max-w-[220px]"
      >
        <svg className="w-4 h-4 mr-1.5 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="truncate">{displayName}</span>
        <svg className="w-4 h-4 ml-1 flex-shrink-0 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 mt-1 w-72 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-1">
            {locations.map((loc) => (
              <button
                key={loc.id}
                onClick={() => handleSwitch(loc)}
                className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                  loc.id === currentLocation.id ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className={`font-medium truncate ${loc.subscribed === false ? 'text-gray-400' : ''}`}>
                    {loc.business_name}
                    {loc.subscribed === false && <span className="text-xs font-normal text-gray-400 ml-1">(unsubscribed)</span>}
                  </div>
                  {loc.street_address && (
                    <div className="text-xs text-gray-500 truncate">
                      {loc.street_address}{loc.city ? `, ${loc.city}` : ''}
                    </div>
                  )}
                </div>
                {loc.id === currentLocation.id && (
                  <svg className="w-4 h-4 flex-shrink-0 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
          <div className="border-t border-gray-200">
            <button
              onClick={handleAddLocation}
              className="w-full text-left px-4 py-2.5 text-sm text-blue-600 hover:bg-blue-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add new location
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSwitcher;
